/** Client-side avatar crop: turn a source image + crop rectangle (in source
 *  pixels, as produced by react-easy-crop's onCropComplete) into a square
 *  256×256 WebP blob. Crop + downscale happen in one drawImage call. */

export const AVATAR_SIZE = 256;

export interface CropPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Issue the single crop+resize drawImage call. Extracted for unit testing. */
export function drawCropToCanvas(
  canvas: HTMLCanvasElement,
  image: CanvasImageSource,
  crop: CropPixels,
  size: number,
): void {
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function getCroppedBlob(imageSrc: string, cropPixels: CropPixels): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  drawCropToCanvas(canvas, image, cropPixels, AVATAR_SIZE);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas.toBlob returned null"))),
      "image/webp",
      0.9,
    );
  });
}
